/**
 * Buildings Layout (predefined layout 8)
 *
 * An accordion of buildings where only one item is open at a time.
 * Opening an item swaps the floorplate image shown in the left column.
 * Mirrors the open/close height animation used by accordion.js.
 */

let activeBuildings = new Set();
let buildingsResizeListenerAdded = false;

/**
 * Initializes building accordions within the given context.
 * @param {HTMLElement|Document} context
 */
export function initializeBuildings(context = document) {
    const layouts = context.querySelectorAll('.buildings');

    layouts.forEach(layout => {
        if (layout.classList.contains('js-buildings-init')) return;
        layout.classList.add('js-buildings-init');

        const items = layout.querySelectorAll('.building');

        items.forEach(item => {
            const title = item.querySelector('.building__title');
            const content = item.querySelector('.building__content');

            if (!title || !content) return;

            if (item.classList.contains('is-open')) {
                openBuilding(item);
                showFloorplate(layout, item.dataset.building);
            } else {
                content.style.height = '0px';
                title.setAttribute('aria-expanded', 'false');
                content.setAttribute('aria-hidden', 'true');
            }

            title.addEventListener('click', (e) => {
                e.preventDefault();
                toggleBuilding(layout, item);
            });

            const closeBtn = item.querySelector('.building__close');
            if (closeBtn) {
                closeBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeBuilding(item);
                    showFloorplate(layout, null);
                });
            }
        });

        const floorplates = layout.querySelectorAll('.buildings__floorplate');

        floorplates.forEach(floorplate => {
            const button = floorplate.querySelector('button');
            if (!button) return;

            const isMatch = floorplate.classList.contains('is-active');
            button.classList.toggle('is-active', isMatch);
            button.setAttribute('aria-expanded', isMatch ? 'true' : 'false');

            button.addEventListener('click', (e) => {
                e.preventDefault();
                const buildingIndex = button.dataset.building || floorplate.dataset.building;
                const targetItem = layout.querySelector(`.building[data-building="${buildingIndex}"]`);

                if (targetItem) {
                    toggleBuilding(layout, targetItem);
                }
            });
        });
    });

    if (!buildingsResizeListenerAdded) {
        window.addEventListener('resize', onBuildingsResize);
        window.addEventListener('orientationchange', onBuildingsResize);
        buildingsResizeListenerAdded = true;
    }
}

/**
 * Cleans up active references. Call before page transitions.
 */
export function destroyBuildings() {
    activeBuildings.clear();
}

function toggleBuilding(layout, item) {
    const isOpen = item.classList.contains('is-open');

    if (isOpen) {
        // Keep one item open at all times to match the design.
        return;
    }

    const siblings = layout.querySelectorAll('.building.is-open');
    siblings.forEach(sibling => {
        if (sibling !== item) {
            closeBuilding(sibling);
        }
    });

    openBuilding(item);
    showFloorplate(layout, item.dataset.building);
}

function openBuilding(item) {
    const content = item.querySelector('.building__content');
    const title = item.querySelector('.building__title');

    if (!content || !title) return;

    item.classList.add('is-open');
    title.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');

    content.style.height = content.scrollHeight + 'px';

    // Recalculate height once any images inside have loaded, so the
    // content isn't clipped when measured before the image is ready.
    content.querySelectorAll('img').forEach(img => {
        if (!img.complete) {
            img.addEventListener('load', () => {
                if (item.classList.contains('is-open')) {
                    content.style.height = content.scrollHeight + 'px';
                }
            }, { once: true });
        }
    });

    activeBuildings.add(item);
}

function closeBuilding(item) {
    const content = item.querySelector('.building__content');
    const title = item.querySelector('.building__title');

    if (!content || !title) return;

    item.classList.remove('is-open');
    title.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');

    content.style.height = '0px';

    activeBuildings.delete(item);
}

function showFloorplate(layout, index) {
    const floorplates = layout.querySelectorAll('.buildings__floorplate');

    floorplates.forEach(floorplate => {
        const isMatch = index !== null && index !== undefined && floorplate.dataset.building === String(index);
        floorplate.classList.toggle('is-active', isMatch);
        floorplate.setAttribute('aria-hidden', isMatch ? 'false' : 'true');

        const button = floorplate.querySelector('button');
        if (button) {
            button.classList.toggle('is-active', isMatch);
            button.setAttribute('aria-expanded', isMatch ? 'true' : 'false');
        }
    });
}

function onBuildingsResize() {
    activeBuildings.forEach(item => {
        const content = item.querySelector('.building__content');
        if (content) {
            content.style.height = 'auto';
            const newHeight = content.scrollHeight;
            requestAnimationFrame(() => {
                content.style.height = newHeight + 'px';
            });
        }
    });
}
